import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { patchRoom } from '../offline/optimistic';
import { pollEvery } from '../realtime/live';
import type { Availability, Booking, DailyReport, Fault, FolioView, Guest, HkStaff, HkTask, JournalEntryView, LedgerAccount, NightAuditStatus, RatePlan, Reconciliation, Room, RoomBlock, RoomType, TaxRule, TrialBalance } from './types';

const qs = (o: Record<string, string | number | undefined | null>): string => {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(o)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
};

export interface BookingFilter {
  status?: string;
  arrivingOn?: string;
  departingOn?: string;
  reference?: string;
  limit?: number;
}

export const useBookings = (f: BookingFilter, enabled = true) =>
  useQuery({ queryKey: ['bookings', f], enabled, queryFn: () => api<{ bookings: Booking[] }>(`/bookings${qs({ ...f })}`).then((r) => r.bookings) });

export const useBooking = (id: string) => useQuery({ queryKey: ['booking', id], queryFn: () => api<Booking>(`/bookings/${id}`) });

export const useFolio = (bookingId: string) => useQuery({ queryKey: ['folio', bookingId], queryFn: () => api<FolioView>(`/bookings/${bookingId}/folio`) });

export const useRooms = () => useQuery({ queryKey: ['rooms'], queryFn: () => api<{ rooms: Room[] }>('/rooms').then((r) => r.rooms) });

export const useAvailability = (checkIn: string, checkOut: string, adults: number, enabled: boolean) =>
  useQuery({ queryKey: ['availability', checkIn, checkOut, adults], enabled, queryFn: () => api<Availability>(`/availability${qs({ checkIn, checkOut, adults })}`) });

export const useGuestSearch = (search: string) =>
  useQuery({ queryKey: ['guests', search], enabled: search.trim().length >= 2, queryFn: () => api<{ guests: Guest[] }>(`/guests${qs({ search: search.trim() })}`).then((r) => r.guests) });

export const useDaily = (date: string | undefined, enabled: boolean) =>
  useQuery({ queryKey: ['daily', date], enabled, queryFn: () => api<DailyReport>(`/reports/daily${qs({ date })}`) });

export const useNightAudit = (enabled: boolean) => useQuery({ queryKey: ['night-audit'], enabled, queryFn: () => api<NightAuditStatus>('/night-audit') });

/** After anything that changes a stay, everything derived from it is stale. */
export function useInvalidateStay() {
  const qc = useQueryClient();
  return (bookingId?: string) => {
    void qc.invalidateQueries({ queryKey: ['bookings'] });
    void qc.invalidateQueries({ queryKey: ['rooms'] });
    void qc.invalidateQueries({ queryKey: ['daily'] });
    void qc.invalidateQueries({ queryKey: ['availability'] });
    if (bookingId) {
      void qc.invalidateQueries({ queryKey: ['booking', bookingId] });
      void qc.invalidateQueries({ queryKey: ['folio', bookingId] });
    }
  };
}

export function useBookingAction(bookingId: string) {
  const inv = useInvalidateStay();
  return useMutation({
    mutationFn: (a: { path: 'confirm' | 'cancel' | 'no-show' | 'check-in' | 'check-out' | 'assign-room'; body?: Record<string, unknown> }) =>
      api<Booking>(`/bookings/${bookingId}/${a.path}`, { method: 'POST', body: a.body ?? {} }),
    onSuccess: () => inv(bookingId),
  });
}

export function useRoomStatus() {
  const inv = useInvalidateStay();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (a: { roomId: string; status: 'VACANT_CLEAN' | 'VACANT_DIRTY' | 'INSPECTED'; number?: string }) =>
      api(`/rooms/${a.roomId}/status`, { method: 'PATCH', body: { status: a.status }, queue: { label: `Mark room ${a.number ?? ''} ${a.status === 'VACANT_DIRTY' ? 'dirty' : a.status === 'INSPECTED' ? 'inspected' : 'clean'}`.replace('  ', ' '), optimistic: () => patchRoom(qc, a.roomId, a.status) } }),
    onSuccess: () => inv(),
  });
}

export function useNightAuditRun() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (date?: string) => api<{ businessDate: string; summary: unknown }>('/night-audit', { method: 'POST', body: date ? { date } : {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['night-audit'] });
      void qc.invalidateQueries({ queryKey: ['daily'] });
    },
  });
}

export const useRoomTypes = () => useQuery({ queryKey: ['room-types'], queryFn: () => api<{ roomTypes: RoomType[] }>('/room-types').then((r) => r.roomTypes) });
export interface BackupList {
  backups: { name: string; size: number; at: string }[];
  lastOk: string | null;
  lastFailed: string | null;
  lastDownloadedAt: string | null;
  downloadDue: boolean;
}
export const useBackups = (enabled: boolean) => useQuery({ queryKey: ['backups'], enabled, queryFn: () => api<BackupList>('/backups') });

export interface SeasonRow {
  id: string;
  name: string;
  from: string;
  to: string;
  upliftBps: number;
  roomTypeIds: string[];
  rates: number;
}
export const useSeasons = () => useQuery({ queryKey: ['seasons'], queryFn: () => api<{ seasons: SeasonRow[] }>('/seasons').then((r) => r.seasons) });
export const useRatePlans = () => useQuery({ queryKey: ['rate-plans'], queryFn: () => api<{ ratePlans: RatePlan[] }>('/rate-plans').then((r) => r.ratePlans) });
export const useTaxRules = () => useQuery({ queryKey: ['tax-rules'], queryFn: () => api<{ taxRules: TaxRule[] }>('/tax-rules').then((r) => r.taxRules) });

/** Anything that changes what can be sold changes availability and quotes too. */
export const useRoomBlocks = () => useQuery({ queryKey: ['room-blocks'], queryFn: () => api<{ blocks: RoomBlock[] }>('/room-blocks').then((r) => r.blocks) });

export function useInvalidateSetup() {
  const qc = useQueryClient();
  return () => {
    for (const k of ['room-types', 'rooms', 'rate-plans', 'tax-rules', 'availability', 'room-blocks', 'seasons']) void qc.invalidateQueries({ queryKey: [k] });
  };
}

export const useLedgerAccounts = () => useQuery({ queryKey: ['ledger-accounts'], queryFn: () => api<{ accounts: LedgerAccount[] }>('/ledger/accounts').then((r) => r.accounts) });
export const useTrialBalance = (from: string, to: string) => useQuery({ queryKey: ['trial-balance', from, to], queryFn: () => api<TrialBalance>(`/ledger/trial-balance${qs({ from, to })}`) });
export const useReconciliation = () => useQuery({ queryKey: ['reconciliation'], queryFn: () => api<Reconciliation>('/ledger/reconciliation') });
export const useJournal = (f: { from?: string; to?: string; accountCode?: string; sourceType?: string }) =>
  useQuery({ queryKey: ['journal', f], queryFn: () => api<{ entries: JournalEntryView[] }>(`/ledger/journal${qs({ ...f, limit: 100 })}`).then((r) => r.entries) });

export function useInvalidateLedger() {
  const qc = useQueryClient();
  return () => {
    for (const k of ['ledger-accounts', 'trial-balance', 'reconciliation', 'journal']) void qc.invalidateQueries({ queryKey: [k] });
  };
}

/* ---------------------------------------------------------------- housekeeping */
export const useHkTasks = () => useQuery({ queryKey: ['hk', 'tasks'], refetchInterval: pollEvery(30_000), queryFn: () => api<{ tasks: HkTask[] }>('/housekeeping/tasks').then((r) => r.tasks) });
export const useHkStaff = (enabled: boolean) => useQuery({ queryKey: ['hk', 'staff'], enabled, queryFn: () => api<{ staff: HkStaff[] }>('/housekeeping/staff').then((r) => r.staff) });
export const useFaults = (status: 'open' | 'RESOLVED' = 'open') =>
  useQuery({ queryKey: ['hk', 'faults', status], refetchInterval: pollEvery(60_000), queryFn: () => api<{ requests: Fault[] }>(`/maintenance${status === 'open' ? '' : `?status=${status}`}`).then((r) => r.requests) });

/** Task and fault changes can move a room's status, so the room board and today's counts refresh too. */
export function useHkMutation<V>(run: (v: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['hk'] });
      void qc.invalidateQueries({ queryKey: ['rooms'] });
      void qc.invalidateQueries({ queryKey: ['daily'] });
    },
  });
}
